import { FilterChoice } from '../../../common/components/SearchHeader/types/FilterChoice';
import {
  renderLocationChoice,
  renderLocationInOrNear,
} from '../../../common/components/SearchHeader/types/LocationChoice';
import { pluralize } from '../../../common/util/pluralize';
import { getSpecialtyPractitionerTerm, getSpecialtyTerm } from '../renderers';
import { SeoScenario } from '../types';

const telehealthTextContent = (it: FilterChoice) => {
  const location = renderLocationChoice(it.location);

  return `As more and more Australians need to be seen remotely via telephone or video conferencing, finding an online doctor that can see you anytime, anywhere has never been easier with Healthengine’s telehealth directory. This directory of online doctors allows you to find healthcare practitioners offering remote consultations across the country and various specialties including GPs, physiotherapists, psychologists etc. with an easy booking process for online doctor appointments.

Whilst remote consultations with online doctors are safe, easy, and convenient, some issues still need you to attend in-person. Healthengine’s telehealth directory also connects you with regular practices offering telehealth services that have a physical location near you. By searching for telehealth services in ${location} on Healthengine, you can find all clinics in and around ${location} that will provide you with necessary healthcare services including remote and online medical consultations.

Healthengine also offers a seamless integrated video consultation and payment experience for the patients of participating practices.`;
};

const telehealthSpecialtyTextContent = (it: FilterChoice) => {
  const practitioner = getSpecialtyPractitionerTerm(it);
  const practitioners = pluralize(practitioner);
  const location = renderLocationChoice(it.location);
  const specialty = getSpecialtyTerm(it);

  return `As more and more Australians need to be seen remotely via telephone or video conferencing finding an online doctor that can see you anytime, anywhere has never been easier with Healthengine’s telehealth directory. This directory of online doctors allows you to find healthcare practitioners offering remote consultations across the country and various specialties, including online ${practitioners} and various other online doctors with an easy booking process for online doctor appointments.

Whilst remote consultations with online doctors are safe, easy, and convenient, some issues still need you to attend in-person. Healthengine’s telehealth directory al connects you with regular practices offering telehealth services that have a physical location near you. By searching for ${practitioners} offering telehealth services in ${location} on Healthengine, you can find all ${specialty} clinics in and around ${location} that will provide you with necessary healthcare services including remote and online medical consultations.

Healthengine also offers a seamless integrated video consultation and payment experience for the patients of participating practices.`;
};

export const telehealthScenario: SeoScenario = it => {
  const location = renderLocationChoice(it.location);

  return {
    pageTitle: `Find Practices with Telehealth services in ${location}`,
    visualHeader: `Practices with Telehealth services in ${location}`,
    metaDescription: `Find a practice with Telehealth services in ${location}. Book an appointment online or view opening hours, 
    contact details, and more in one easy location. Finding a practice with Telehealth services near you in ${location} has never been easier with Healthengine`,
    textContent: telehealthTextContent(it),
  };
};

export const telehealthAppointmentScenario: SeoScenario = it => {
  const location = renderLocationChoice(it.location);
  const inOrNear = renderLocationInOrNear(it.location);
  return {
    pageTitle: `Book Practices with Telehealth appointments near you in ${location}`,
    visualHeader: `Practices with Telehealth appointments ${inOrNear} ${location}`,
    metaDescription: `Find a practice in ${location} and book a Telehealth appointment online instantly. 1000s of appointments with trusted practitioners available every day. 
      Book a Telehealth appointment online near you with Healthengine and find trusted practitioners in ${location}.`,
    textContent: telehealthTextContent(it),
  };
};

export const telehealthSpecialtyScenario: SeoScenario = it => {
  const location = renderLocationChoice(it.location);
  const practitioner = getSpecialtyPractitionerTerm(it);
  const practitioners = pluralize(practitioner);
  const inOrNear = renderLocationInOrNear(it.location);

  return {
    pageTitle: `Find ${practitioners} with Telehealth services ${inOrNear} ${location}`,
    visualHeader: `${practitioners} with Telehealth services ${inOrNear} ${location}`,
    metaDescription: `Find a ${practitioner} with Telehealth services in ${location}. Book an appointment 
    online or view opening hours, contact details, and more in one easy location. Finding a ${practitioner} with Telehealth services 
    near you in ${location} has never been easier with Healthengine`,
    textContent: telehealthSpecialtyTextContent(it),
  };
};

export const telehealthSpecialtyAppointmentsScenario: SeoScenario = it => {
  const location = renderLocationChoice(it.location);
  const specialty = getSpecialtyTerm(it);
  const practitioner = getSpecialtyPractitionerTerm(it);
  const practitioners = pluralize(practitioner);
  const inOrNear = renderLocationInOrNear(it.location);

  return {
    pageTitle: `Book ${specialty} Telehealth appointments near you ${inOrNear} ${location}`,
    visualHeader: `${specialty} Telehealth appointments ${inOrNear} ${location}`,
    metaDescription: `Find a ${practitioner} in ${location} and book a Telehealth appointment online instantly. 
    1000s of appointments with trusted practitioners available every day. Book a ${practitioner} Telehealth appointment online near 
    you with Healthengine and find trusted ${practitioners} in ${location}`,
    textContent: telehealthSpecialtyTextContent(it),
  };
};

export const telehealthDirectoryGp: SeoScenario = it => {
  const location = renderLocationChoice(it.location);
  const hasLocationFilter = Boolean(it.location);
  const inOrNear = renderLocationInOrNear(it.location);

  return {
    pageTitle: `Find Online GPs with Telehealth services ${inOrNear} ${location}`,
    visualHeader: hasLocationFilter
      ? `Find an Online GP ${inOrNear} ${location}`
      : 'Online GP in Australia',
    metaDescription: `Find an Online GP with Telehealth services in ${location}. Book an appointment
    online or view opening hours, contact details, and more in one easy location. Finding an Online GP with Telehealth services 
    near you in ${location} has never been easier with Healthengine`,
    textContent: (() => {
      if (!hasLocationFilter) {
        return `Finding an online GP in Australia who’s right for you and offer’s bulk billed online GP appointments is easy with Healthengine.

        Healthengine is Australia’s largest medical booking app that helps thousands of patients book online GP appointments daily, and it can be used to book a general physician if you need medical attention from your home, especially when it is a prominent issue and you can’t visit the GP. If you have access to an online GP, you can get the help that you need from a regular GP with any fuss or worry.
        
        If you have a medical condition or you just need some general health care for an ongoing problem that you may have, calling an online GP would be of benefit to you. Whether you’re in need of an online GP in Sydney, online GP in Melbourne, online GP in Perth, online GP in Brisbane, or anywhere else in Australia, we can help you find everything in one place.
        
        Online GPs in Australia can work different times of the day such as weekdays during normal business hours as well as outside of business hours to take care of all of their patients.
        
        There are also online GPs that work on weekends in case someone needs assistance when most surgeries may not be open. These services are great for people that do not want to spend several hours waiting in the emergency room for help with a health issue, but still need to see a general practitioner as soon as possible for whatever reason. You can easily find bulk-billed online GP appointments in Australia through Healthengine.`;
      }

      if (it.location?.metro) {
        return `Finding an online GP in ${location} who's right for you is easy with Healthengine.

        We provide comprehensive information on online GPs and medical centres available in and around ${location}, including real positive customer feedback, accreditations, contact details, staff profiles, opening hours, pricing and payment info and much more.
        
        Easily find bulk billed appointments with online GPs, we connect you with the best online doctors and medical clinics in ${location} when you need it the most.`;
      }

      return `Finding an online GP in ${location} who's right for you is easy with Healthengine.

      We provide comprehensive information on online GPs and medical centres available in and around ${location}, including real positive customer feedback, accreditations, contact details, staff profiles, opening hours, pricing and payment info and much more.
      
      Easily find bulk billed appointments with online GPs, we connect you with the best online doctors and medical clinics in ${location} when you need it the most.`;
    })(),
  };
};

export const telehealthAppointmentGp: SeoScenario = it => {
  const location = renderLocationChoice(it.location);
  const hasLocationFilter = Boolean(it.location);
  const inOrNear = renderLocationInOrNear(it.location);

  return {
    pageTitle: `Book Online Doctor Telehealth appointments near you in ${location}`,
    visualHeader: hasLocationFilter
      ? `Find an Online Doctor ${inOrNear} ${location}`
      : `Online Doctor ${inOrNear} ${location}`,
    metaDescription: `Find an Online Doctor in ${location} and book a Telehealth appointment online instantly.
    1000s of appointments with trusted practitioners available every day. Book a Telehealth appointment online near 
    you with Healthengine and find trusted Online Doctors in ${location}`,
    textContent: (() => {
      if (!hasLocationFilter) {
        return `Finding an online doctor in Australia who’s right for you and offer’s bulk billed online doctor appointments is easy with Healthengine.

        Healthengine is Australia’s largest medical booking app that helps thousands of patients book online doctor appointments daily, and it can be used to book a doctor if you need medical attention from your home, especially when it is a prominent issue and you can’t visit the doctor. If you have access to an online doctor, you can get the help that you need from a regular GP with any fuss or worry.
        
        If you have a medical condition or you just need some general health care for an ongoing problem that you may have, calling an online doctor would be of benefit to you.
        
        Online doctors in Australia can work different times of the day such as weekdays during normal business hours as well as outside of business hours to take care of all of their patients.
        
        There are also online doctors that work on weekends in case someone needs assistance on when most surgeries may not be open. Online doctors may also have a registered nurse on call that can assist them in the treatment of their patients to help with the medical care required. These services are great for people that do not want to spend several hours waiting in the emergency room for help with a health issue, but still need to see a general practitioner as soon as possible for whatever reason. You can easily find bulk-billed online doctor appointments in Australia through Healthengine.`;
      }

      if (it.location?.metro) {
        return `Finding an online doctor in ${location} who's right for you is easy with Healthengine.

        We provide comprehensive information on online GPs and medical centres available in and around ${location}, including real positive customer feedback, accreditations, contact details, staff profiles, opening hours, pricing and payment info and much more.
        
        Easily find bulk billed appointments with online GPs, we connect you with the best online doctors and medical clinics in ${location} when you need it the most.`;
      }

      return `Finding an online doctor in ${location} who's right for you is easy with Healthengine.

      We provide comprehensive information on online doctors and medical centres available in and around ${location}, including real positive customer feedback, accreditations, contact details, staff profiles, opening hours, pricing and payment info and much more.
      
      Easily find bulk billed appointments with online doctors, we connect you with the best online doctors and medical clinics in ${location} when you need it the most.`;
    })(),
  };
};
